import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  History,
  Calendar,
  Clock,
  AlertTriangle,
  RefreshCw,
  FileText,
  ChevronRight,
  Car
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const SessionHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [actionLogs, setActionLogs] = useState([]);

  useEffect(() => {
    fetchSessions();
    fetchActionLogs();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions/history`);
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
    }
  };

  const fetchActionLogs = async () => {
    try {
      const response = await axios.get(`${API}/logs/actions`);
      setActionLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch action logs:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startDate, endDate) => {
    if (!endDate) return "In Progress";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.floor((end - start) / 1000 / 60);
    if (diff < 60) return `${diff} minutes`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  const getActionIcon = (type) => {
    switch (type) {
      case "reset": return <RefreshCw className="h-4 w-4 text-[#007AFF]" />;
      case "reprogram": return <FileText className="h-4 w-4 text-[#FF9F0A]" />;
      case "dtc_clear": return <AlertTriangle className="h-4 w-4 text-[#30D158]" />;
      default: return <FileText className="h-4 w-4 text-white/50" />;
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Session History</h1>
          <p className="header-subtitle">Previous diagnostic sessions and action logs</p>
        </div>
        
        <Button
          onClick={() => { fetchSessions(); fetchActionLogs(); }}
          variant="outline"
          className="bg-transparent border-white/20 text-white hover:bg-white/5"
          data-testid="refresh-history-btn"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sessions List */}
        <div className="col-span-8">
          <div className="bento-card">
            <div className="card-header">
              <h2 className="card-title">Diagnostic Sessions</h2>
              <span className="text-xs text-white/50 font-mono">{sessions.length} sessions</span>
            </div>
            
            {sessions.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No diagnostic sessions recorded</p>
                <p className="text-white/30 text-sm mt-1">Sessions will appear here after connecting to a vehicle</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-4 glass-card hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => setSelectedSession(session)}
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 glass-card rounded">
                          <Car className="h-5 w-5 text-[#007AFF]" />
                        </div>
                        <div>
                          <p className="font-mono text-[#007AFF]">{session.vin}</p>
                          <p className="text-white/50 text-sm">
                            {formatDate(session.started_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-white/50 uppercase">Duration</p>
                          <p className="font-mono text-sm text-white">
                            {formatDuration(session.started_at, session.ended_at)}
                          </p>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-xs text-white/50 uppercase">DTCs Found</p>
                          <p className="font-mono text-sm text-[#FF9F0A]">{session.dtcs_found || 0}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-xs text-white/50 uppercase">Modules Reset</p>
                          <p className="font-mono text-sm text-[#30D158]">{session.modules_reset || 0}</p>
                        </div>
                        
                        <ChevronRight className="h-5 w-5 text-white/30" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Actions */}
        <div className="col-span-4">
          <div className="bento-card">
            <div className="card-header">
              <h2 className="card-title">Recent Actions</h2>
            </div>
            
            {actionLogs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No actions recorded</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {actionLogs.slice(0, 20).map((log, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 glass-card">
                    {getActionIcon(log.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white">{log.module_name}</p>
                      <p className="text-xs text-white/50 capitalize">{log.type}</p>
                    </div>
                    <p className="text-xs text-white/40 font-mono">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session Detail Dialog */}
      {selectedSession && (
        <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase tracking-wider">
                Session Details
              </DialogTitle>
              <DialogDescription className="text-white/60">
                VIN: <span className="text-[#007AFF] font-mono">{selectedSession.vin}</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Session Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 glass-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-[#007AFF]" />
                    <span className="text-xs text-white/50 uppercase">Started</span>
                  </div>
                  <p className="font-mono text-sm text-white">{formatDate(selectedSession.started_at)}</p>
                </div>
                
                <div className="p-4 glass-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-[#007AFF]" />
                    <span className="text-xs text-white/50 uppercase">Duration</span>
                  </div>
                  <p className="font-mono text-sm text-white">
                    {formatDuration(selectedSession.started_at, selectedSession.ended_at)}
                  </p>
                </div>
                
                <div className="p-4 glass-card">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-[#FF9F0A]" />
                    <span className="text-xs text-white/50 uppercase">DTCs</span>
                  </div>
                  <p className="font-mono text-sm text-white">
                    Found: {selectedSession.dtcs_found || 0} / Cleared: {selectedSession.dtcs_cleared || 0}
                  </p>
                </div>
              </div>
              
              {/* Actions Performed */}
              <div>
                <h4 className="text-sm text-white/50 uppercase tracking-wider mb-3">Actions Performed</h4>
                {selectedSession.actions && selectedSession.actions.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedSession.actions.map((action, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 glass-card">
                        {getActionIcon(action.type)}
                        <div className="flex-1">
                          <p className="text-sm text-white capitalize">{action.type.replace('_', ' ')}</p>
                          {action.module_name && (
                            <p className="text-xs text-white/50">{action.module_name}</p>
                          )}
                        </div>
                        <p className="text-xs text-white/40 font-mono">
                          {new Date(action.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm">No actions recorded for this session</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SessionHistory;
